import { useMemo, useState } from 'react';
import { tokens, Text, TextInput, Button, Stack, Loader, Link, Alert } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { GenericFilterDropdown } from '../GenericBloc/GenericFilter/GenericFilterDropdown';
import { GenericFilterHeader } from '../GenericBloc/GenericFilter/GenericFilterHeader';
import type { Experiment } from '../../../../domains/experiments/model/Experiment';

const Container = styled.div`
  padding: ${tokens.spacer.x2} 0 0 0;
`;

const TextInputContainer = styled.div`
  padding: ${tokens.spacer.x2};
`;

const AlertContainer = styled.div`
  padding: 0 ${tokens.spacer.x2} ${tokens.spacer.x2} ${tokens.spacer.x2};
`;

const NoResultDiv = styled.div`
  border-top: 1px solid ${tokens.color.ui_border_light};
  text-align: center;
  padding: ${tokens.spacer.x2};
`;

const LoaderContainer = styled.div`
  padding: ${tokens.spacer.x2};
`;

const StyledButton = styled(Button)`
  ${tokens.text('body2')}
  min-height: ${tokens.spacer.x4};
  border-radius: 0;
`;

const ExperimentTitle = styled(Text)`
  padding-left: ${tokens.spacer.x2};
`;

const StyledStack = styled(Stack)`
  border-top: 1px solid ${tokens.color.ui_border_light};
  max-height: 300px;
  overflow-y: scroll;
`;

interface ExperimentSelectorProps {
  filterName: string;
  optionList: Experiment[];
  optionListIsLoading: boolean;
  onExperimentSelected: (experiementOption: Experiment) => void;
  customWidth?: number;
  isIntegrationActive: boolean;
}

const MORE_INFO_OPTIMIZELY_URL = '/articles/115009347067-Using-Hotjar-With-Optimizely';
const MORE_INFO_GOOGLE_URL = 'https://optimize.google.com/optimize/home/';
const ACTIVATE_INTEGRATION_URL = '/settings/integrations';

export function ExperimentSelector({
  filterName,
  optionList,
  optionListIsLoading,
  onExperimentSelected,
  customWidth,
  isIntegrationActive,
}: ExperimentSelectorProps): JSX.Element {
  const helpHotjarUrl = useHelpHotjarUrl();
  const [searchInput, setSearchInput] = useState('');
  const filteredExperimentOptionList = useMemo(() => {
    if (optionListIsLoading) {
      return [];
    }
    if (searchInput) {
      return optionList.filter((item) =>
        item.experimentId.toLocaleLowerCase().includes(searchInput),
      );
    }
    return optionList;
  }, [optionListIsLoading, searchInput, optionList]);

  return (
    <GenericFilterDropdown ariaLabel={`${filterName} dropdown`} customWidth={customWidth}>
      <Container>
        <ExperimentTitle textType="subtitle2">
          {filterName === 'Unbounce' ? 'Page name' : 'Experiment'}
        </ExperimentTitle>
        <GenericFilterHeader>
          {filterName === 'Optimizely' && (
            <span>
              Select an Optimizely experiment or search for one by name.{' '}
              <Link kind="inline" href={`${helpHotjarUrl}${MORE_INFO_OPTIMIZELY_URL}`} isExternal>
                More about using Hotjar with Optimizely
              </Link>
            </span>
          )}
          {filterName === 'Google Optimize' && (
            <span>
              The unique ID Google Optimize creates for your experiments. Find it on the{' '}
              <Link kind="inline" href={MORE_INFO_GOOGLE_URL} isExternal>
                experiment details page
              </Link>
            </span>
          )}
          {filterName === 'Unbounce' && (
            <span>
              Select the Unbounce page you’re running experiments on to filter by its variants.
            </span>
          )}
        </GenericFilterHeader>
        {optionListIsLoading && (
          <LoaderContainer>
            <Loader size="l" isGreyscale />
          </LoaderContainer>
        )}
        {!optionListIsLoading && (
          <>
            <TextInputContainer>
              <TextInput
                id="searchInput"
                name="Search experiments"
                icon="search"
                placeholder={`Search for ${filterName === 'Unbounce' ? 'page' : 'experiment'}`}
                size="m"
                defaultValue={searchInput}
                onChange={(event) => setSearchInput(event.target.value.toLowerCase().trim())}
                autoComplete="off"
                isDisabled={!isIntegrationActive}
              />
            </TextInputContainer>
            {!isIntegrationActive && (
              <AlertContainer>
                <Alert
                  kind="info"
                  actionText={`Connect ${filterName}`}
                  actionHref={ACTIVATE_INTEGRATION_URL}
                  actionProps={{ isExternal: true }}
                  direction="column"
                >
                  To use this integration, you&apos;ll need to connect it first.
                </Alert>
              </AlertContainer>
            )}
            {isIntegrationActive && filteredExperimentOptionList.length > 0 && (
              <StyledStack direction="column" spacing="x0">
                {filteredExperimentOptionList.map((item) => (
                  <StyledButton
                    kind="ghost"
                    size="s"
                    key={item.experimentId}
                    onClick={() => onExperimentSelected(item)}
                  >
                    {`# ${item.experimentId}`}
                  </StyledButton>
                ))}
              </StyledStack>
            )}
            {isIntegrationActive && filteredExperimentOptionList.length === 0 && (
              <NoResultDiv>
                <Text textType="body2">
                  {filterName === 'Unbounce' ? 'No pages match' : 'No experiments match'}
                </Text>
              </NoResultDiv>
            )}
          </>
        )}
      </Container>
    </GenericFilterDropdown>
  );
}
